import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../context/AppContext";
import { ChatMessages } from "./chat/ChatMessages";
import { ChatComposer } from "./chat/ChatComposer";
import { LabLauncher } from "./chat/LabLauncher";

// Chat state is tab-scoped (sessionStorage): it is cleared when the tab closes and never outlives the visit.
const SS_KEY = "bl_chat_session";
const MSG_KEY = "bl_chat_messages";

const SEED = [
  { role: "assistant", text: "Hi — I'm the Bio Labz support assistant. Ask about products, shipping, testing or your account. I can't give medical or dosing advice." },
];
const seeded = () => SEED.map((m, i) => ({ ...m, id: `seed-${i}` }));

function loadMessages() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(MSG_KEY) || "null");
    if (!stored) return seeded();
    return stored.map((m, i) => (m.id ? m : { ...m, id: `${Date.now()}-${i}` }));
  } catch { return seeded(); }
}

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => { sessionStorage.setItem(MSG_KEY, JSON.stringify(msgs)); }, [msgs]);
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, msgs]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs(prev => [...prev, { id: `u-${Date.now()}`, role: "user", text }]);
    setSending(true);
    try {
      const session_id = sessionStorage.getItem(SS_KEY) || undefined;
      const r = await axios.post(`${API}/chat`, { message: text, session_id });
      if (r.data?.session_id) sessionStorage.setItem(SS_KEY, r.data.session_id);
      setMsgs(prev => [...prev, { id: `a-${Date.now()}`, role: "assistant", text: r.data?.reply || "…" }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Support is offline right now — please try again shortly or use the Contact page.";
      setMsgs(prev => [...prev, { id: `e-${Date.now()}`, role: "assistant", text: detail }]);
    } finally { setSending(false); }
  };

  const reset = () => {
    sessionStorage.removeItem(SS_KEY);
    setMsgs(seeded());
  };

  return (
    <>
      <LabLauncher open={open} onToggle={() => setOpen(v => !v)} />

      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-32 right-4 sm:right-6 z-40 w-[min(94vw,400px)] h-[min(72vh,600px)] bl-glass border border-[#38D2FF]/30 rounded-xl overflow-hidden flex flex-col"
          style={{ boxShadow: "0 0 30px rgba(56,210,255,0.15), 0 24px 48px rgba(0,0,0,0.6)" }}
        >
          <div className="px-5 py-4 border-b border-white/8 flex items-center gap-3 bg-gradient-to-b from-[#E4EAF3]/10 to-transparent">
            <div className="w-2 h-2 rounded-full bg-[color:var(--bl-green)]" style={{ boxShadow: "0 0 8px var(--bl-green-soft)" }} />
            <div className="flex-1">
              <div className="font-display uppercase tracking-[0.2em] text-white text-sm">Lab Support</div>
              <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Online · AI Assistant</div>
            </div>
            <button onClick={reset} title="New conversation" data-testid="chat-reset" className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40 hover:text-white transition-colors">Reset</button>
          </div>

          <ChatMessages msgs={msgs} sending={sending} listRef={listRef} />
          <ChatComposer input={input} setInput={setInput} sending={sending} onSend={send} />

          <div className="px-4 py-2 text-center font-mono text-[10px] tracking-[0.2em] text-white/40 bg-[#02060F]/60 border-t border-white/5">
            Info only. No medical or dosing advice.
          </div>
        </div>
      )}
    </>
  );
}
